import { AgChartTheme } from "ag-charts-enterprise";
import { useEffect, useState } from "react";
import { semanticThemeTokens } from "../..";
import { fontMonoFamily } from "../../theme/Fonts.css";
import { useTheme } from "../../themeSwitcher/ThemeContext";
import { resolveCssVar } from "../../utils/resolveCssVar";
import { noData } from "../Chart.css";

const NO_DATA_TEXT = "No Data to Show";

/**
 * Creates a DOM element to display when there is no data in the chart.
 */

function createNoDataElement(): HTMLElement {
  const element = document.createElement("div");
  element.className = noData;
  element.textContent = NO_DATA_TEXT;
  return element;
}

export function useAgChartTheme(isLoading: boolean): AgChartTheme | null {
  const { theme } = useTheme();
  const [chartTheme, setChartTheme] = useState<AgChartTheme | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Wait for fonts + CSS variables before building the theme.
    // AG Charts caches text measurements on mount — if the custom font
    // hasn't loaded, title dimensions are wrong and stay wrong.
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady
      .catch(() => {})
      .then(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          setChartTheme(buildChartTheme(isLoading));
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isLoading, theme]);

  return chartTheme;
}

function buildChartTheme(isLoading: boolean): AgChartTheme {
  return {
    palette: {
      fills: [
        resolveCssVar(semanticThemeTokens.chart.palette.fill[1]),
        resolveCssVar(semanticThemeTokens.chart.palette.fill[2]),
        resolveCssVar(semanticThemeTokens.chart.palette.fill[3]),
        resolveCssVar(semanticThemeTokens.chart.palette.fill[4]),
        resolveCssVar(semanticThemeTokens.chart.palette.fill[5])
      ],
      strokes: [
        resolveCssVar(semanticThemeTokens.chart.palette.stroke[1]),
        resolveCssVar(semanticThemeTokens.chart.palette.stroke[2]),
        resolveCssVar(semanticThemeTokens.chart.palette.stroke[3]),
        resolveCssVar(semanticThemeTokens.chart.palette.stroke[4]),
        resolveCssVar(semanticThemeTokens.chart.palette.stroke[5])
      ]
    },

    params: {
      fontFamily: fontMonoFamily,
      foregroundColor: resolveCssVar(semanticThemeTokens.chart.foregroundColor),
      backgroundColor: "transparent",
      chromeBackgroundColor: resolveCssVar(
        semanticThemeTokens.chart.chromeBackgroundColor
      ),
      chromeSubtleTextColor: resolveCssVar(
        semanticThemeTokens.chart.chromeSubtleTextColor
      ),
      textColor: resolveCssVar(semanticThemeTokens.chart.textColor),
      tooltipBackgroundColor: resolveCssVar(
        semanticThemeTokens.chart.tooltipBackground
      ),
      tooltipTextColor: resolveCssVar(
        semanticThemeTokens.chart.tooltipTextColor
      ),
      crosshairLabelBackgroundColor: resolveCssVar(
        semanticThemeTokens.chart.crosshairLabelBackgroundColor
      ),
      crosshairLabelTextColor: resolveCssVar(
        semanticThemeTokens.chart.crosshairLabelTextColor
      ),
      gridLineColor: resolveCssVar(semanticThemeTokens.chart.gridColor),
      borderColor: resolveCssVar(semanticThemeTokens.chart.gridColor)
    },

    overrides: {
      common: {
        title: {
          fontSize: 14,
          color: resolveCssVar(semanticThemeTokens.chart.textColor),
          textAlign: "center",
          spacing: 30
        },
        subtitle: {
          fontSize: 14,
          color: resolveCssVar(semanticThemeTokens.chart.textColor),
          textAlign: "center",
          spacing: 16
        },
        axes: {
          number: {
            label: {
              spacing: 8,
              avoidCollisions: true,
              fontSize: 12
            },
            title: {
              fontSize: 12,
              spacing: 16
            }
          },
          category: {
            label: {
              color: resolveCssVar(semanticThemeTokens.chart.textColor),
              fontSize: 12
            },
            line: {
              stroke: resolveCssVar(semanticThemeTokens.chart.gridColor)
            },
            tick: { stroke: resolveCssVar(semanticThemeTokens.chart.gridColor) }
          },
          time: {
            label: {
              color: resolveCssVar(semanticThemeTokens.chart.textColor),
              fontSize: 12
            },
            line: {
              stroke: resolveCssVar(semanticThemeTokens.chart.gridColor)
            },
            tick: { stroke: resolveCssVar(semanticThemeTokens.chart.gridColor) }
          }
        },

        overlays: {
          loading: { enabled: isLoading },
          noVisibleSeries: {
            renderer: createNoDataElement
          },
          noData: {
            renderer: createNoDataElement
          }
        },

        legend: {
          spacing: 16
        },

        // Always above the pointer, never flipped to another side.
        tooltip: {
          position: { offset: 8, placement: "top" }
        },

        // A wheel over the series area scrolls the page, not the chart.
        scrollbar: { enableSeriesAreaScrolling: false }
      }
    }
  };
}
